import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  X,
  Send,
  Loader2,
  MessageSquarePlus,
  ShieldCheck,
  History
} from 'lucide-react';

import { useAdmin } from '@lok/context/AdminContext';
import {
  useGetAdminAgentHealthQuery,
  useInvokeAdminAgentToolMutation,
  useListAdminAuditLogQuery
} from '@lok/services/adminAgent';

import AgentSidebar from './AgentSidebar';
import PreviewCard from './PreviewCard';
import ToolResultCard from './ToolResultCard';

// Persistent floating chat dock that mounts only for superusers.
//
// The dock has two operating modes, signalled by /agent/health/.llm_configured:
//   * BASIC (default): no LLM provider is configured server-side. Supports a
//     small fixed vocabulary plus the per-row preview buttons on the Users
//     page. Clear help text on anything unrecognized.
//   * CONVERSATIONAL (future): an Anthropic key is wired and the loop is
//     built. The mode banner switches automatically when the backend flag
//     flips — keeps the UI honest the day it ships.
const BASIC_SUGGESTIONS = [
  { label: 'Search: lori', value: 'lori' },
  { label: 'Show recent admin actions', value: 'recent actions' }
];

const CONVERSATIONAL_SUGGESTIONS = [
  { label: 'Where is Lori added to?', value: 'Where is Lori added to?' },
  { label: 'Find users with @harvard.edu emails', value: 'Find users with @harvard.edu emails' },
  { label: 'Show recent admin actions', value: 'recent actions' }
];

const routeBasicCommand = (text) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return null;
  if (/^(recent (admin )?actions?|audit log|history)$/i.test(trimmed)) {
    return { tool: 'list_recent_admin_actions', params: { limit: 25 } };
  }
  const looksLikeSearch =
    /^[\w.+@-]+$/.test(trimmed) ||
    (trimmed.split(/\s+/).length <= 3 && !/\?/.test(trimmed));
  if (looksLikeSearch) {
    return { tool: 'semantic_search', params: { query: trimmed, scope: 'users', limit: 10 } };
  }
  return null;
};

const BASIC_HELP_TEXT =
  'Basic mode supports: a name or email to search, "recent actions" to see the audit log, ' +
  'and per-row buttons on the Users page to preview mutations.';

const ChatDock = () => {
  const {
    isSuperAdmin,
    agentDockOpen,
    closeAgentDock,
    toggleAgentDock,
    agentSession,
    agentRequest,
    consumeAgentRequest
  } = useAdmin();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const scrollRef = useRef(null);

  const { data: health } = useGetAdminAgentHealthQuery(undefined, {
    skip: !isSuperAdmin
  });
  const { data: auditLog } = useListAdminAuditLogQuery(
    { limit: 25 },
    { skip: !isSuperAdmin || !agentDockOpen, pollingInterval: 30_000 }
  );
  const [invokeTool, { isLoading: isInvoking }] = useInvokeAdminAgentToolMutation();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isInvoking]);

  const scope = useMemo(
    () => ({ read: 'all clients', write: 'previewed mutations only' }),
    []
  );

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text) return;
    setInput('');
    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}-u`, role: 'user', text }
    ]);

    const routed = routeBasicCommand(text);
    if (!routed) {
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-h`, role: 'agent', help: BASIC_HELP_TEXT }
      ]);
      return;
    }
    try {
      const resp = await invokeTool({ name: routed.tool, params: routed.params }).unwrap();
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-a`, role: 'agent', tool: routed.tool, result: resp?.result }
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-e`,
          role: 'agent',
          error: err?.detail || err?.data?.error || err?.message || 'Tool invocation failed.'
        }
      ]);
    }
  };

  const previewAction = async ({ action, params, narration }) => {
    if (narration) {
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-u`, role: 'user', text: narration }
      ]);
    }
    try {
      const resp = await invokeTool({
        name: 'preview_action',
        params: { action, params }
      }).unwrap();
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-p`,
          role: 'agent',
          tool: 'preview_action',
          preview: resp?.result,
          previewParams: params,
          previewAction: action
        }
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-e`,
          role: 'agent',
          error: err?.detail || err?.data?.error || err?.message || 'Preview failed.'
        }
      ]);
    }
  };

  const confirmExecute = async (messageId, payload) => {
    try {
      const resp = await invokeTool({ name: 'execute_action', params: payload }).unwrap();
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                executed: true,
                executeResult: resp?.result,
                auditLogId: resp?.audit_log_id
              }
            : m
        )
      );
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                executeError:
                  err?.detail || err?.data?.error || err?.message || 'Execute failed.'
              }
            : m
        )
      );
    }
  };

  const cancelPreview = (messageId) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, cancelled: true } : m))
    );
  };

  const lastConsumedRef = useRef(null);
  useEffect(() => {
    if (!agentRequest || agentRequest.id === lastConsumedRef.current) return;
    lastConsumedRef.current = agentRequest.id;
    if (agentRequest.kind === 'preview') {
      previewAction({
        action: agentRequest.action,
        params: agentRequest.params,
        narration: agentRequest.narration
      });
    }
    consumeAgentRequest();
  }, [agentRequest, consumeAgentRequest]);

  if (!isSuperAdmin) return null;

  if (!agentDockOpen) {
    return (
      <button
        type='button'
        onClick={toggleAgentDock}
        className='fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full bg-primary-green px-5 py-3 text-sm font-semibold text-primary-green-fg shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 transition-all'
        aria-label='Open admin agent'
      >
        <Sparkles className='h-4 w-4' />
        Ask the agent
      </button>
    );
  }

  return (
    <div
      className='fixed bottom-6 right-6 z-50 flex h-[640px] max-h-[calc(100vh-3rem)] w-[760px] max-w-[calc(100vw-3rem)] overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1D2838] shadow-2xl'
      role='dialog'
      aria-label='Admin agent'
    >
      <div className='flex flex-1 flex-col min-w-0'>
        <header className='flex items-center justify-between border-b border-gray-100 dark:border-gray-700 px-4 py-3 bg-gray-50/60 dark:bg-[#0F1A2A]'>
          <div className='flex items-center gap-2.5 min-w-0'>
            <span className='inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-green/20 text-primary-green'>
              <Sparkles className='h-4 w-4' />
            </span>
            <div className='flex flex-col min-w-0'>
              <span className='flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white'>
                Continuum admin agent
                <ModeBadge llmConfigured={health?.llm_configured} />
              </span>
              <span className='inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400'>
                <ShieldCheck className='h-3 w-3' />
                Read: {scope.read} · Write: {scope.write}
                {health?.status === 'ok' ? (
                  <span className='ml-1 inline-flex items-center gap-1'>
                    <span className='h-1.5 w-1.5 rounded-full bg-emerald-500' />
                    online
                  </span>
                ) : null}
              </span>
            </div>
          </div>
          <div className='flex items-center gap-1'>
            <IconBtn onClick={() => setShowSidebar((p) => !p)} title={showSidebar ? 'Hide history' : 'Show history'}>
              <History className='h-4 w-4' />
            </IconBtn>
            <IconBtn onClick={() => setMessages([])} title='New conversation'>
              <MessageSquarePlus className='h-4 w-4' />
            </IconBtn>
            <IconBtn onClick={closeAgentDock} title='Close'>
              <X className='h-4 w-4' />
            </IconBtn>
          </div>
        </header>

        <div ref={scrollRef} className='flex-1 space-y-3 overflow-y-auto px-4 py-4'>
          {messages.length === 0 ? (
            <EmptyConversation
              llmConfigured={!!health?.llm_configured}
              suggestions={health?.llm_configured ? CONVERSATIONAL_SUGGESTIONS : BASIC_SUGGESTIONS}
              onPick={(s) => setInput(s)}
            />
          ) : (
            messages.map((m) => (
              <Bubble
                key={m.id}
                message={m}
                onNavigate={navigate}
                onConfirm={(payload) => confirmExecute(m.id, payload)}
                onCancel={() => cancelPreview(m.id)}
                isExecuting={isInvoking}
              />
            ))
          )}
          {isInvoking ? (
            <div className='flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400'>
              <Loader2 className='h-3 w-3 animate-spin text-primary-green' />
              Running tool...
            </div>
          ) : null}
        </div>

        <form
          onSubmit={handleSubmit}
          className='flex items-center gap-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-[#0F1A2A] px-4 py-3'
        >
          <input
            type='text'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              health?.llm_configured
                ? 'Ask about any user, course, or client...'
                : 'Type a name, email, or "recent actions"'
            }
            className='h-10 flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1D2838] px-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-primary-green focus:ring-1 focus:ring-primary-green focus:outline-none'
          />
          <button
            type='submit'
            aria-label='Send message'
            disabled={!input.trim() || isInvoking}
            className='inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary-green text-primary-green-fg hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed transition'
            title='Send'
          >
            <Send className='h-4 w-4' />
          </button>
        </form>
      </div>
      {showSidebar ? (
        <AgentSidebar
          auditLog={auditLog?.results || []}
          sessionId={agentSession?.id}
        />
      ) : null}
    </div>
  );
};

const IconBtn = ({ onClick, title, children }) => (
  <button
    type='button'
    onClick={onClick}
    title={title}
    aria-label={title}
    className='inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-white transition'
  >
    {children}
  </button>
);

const ModeBadge = ({ llmConfigured }) => (
  <span
    className={
      llmConfigured
        ? 'rounded-full bg-primary-green/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-green'
        : 'rounded-full bg-amber-100 dark:bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300'
    }
    title={
      llmConfigured
        ? 'Conversational mode: free-text queries route through Claude.'
        : 'Basic mode: no LLM configured. The dock supports a small fixed vocabulary.'
    }
  >
    {llmConfigured ? 'Conversational' : 'Basic'}
  </span>
);

const EmptyConversation = ({ llmConfigured, suggestions, onPick }) => (
  <div className='flex h-full flex-col items-center justify-center gap-4 text-center text-sm'>
    <span className='inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-green/20 text-primary-green'>
      <Sparkles className='h-5 w-5' />
    </span>
    <div className='max-w-sm'>
      {llmConfigured ? (
        <>
          <p className='font-semibold text-gray-900 dark:text-white'>
            Ask any cross-tenant question.
          </p>
          <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
            Every mutation previews before it runs. Read-only queries are free.
          </p>
        </>
      ) : (
        <>
          <p className='font-semibold text-gray-900 dark:text-white'>Basic mode.</p>
          <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
            Type a name or email to search, or &ldquo;recent actions&rdquo; for the audit
            log. Per-row buttons on the Users page open the preview gate.
          </p>
        </>
      )}
    </div>
    <div className='flex flex-col gap-2'>
      {suggestions.map((s) => (
        <button
          type='button'
          key={s.value}
          onClick={() => onPick(s.value)}
          className='rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0F1A2A] px-3.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-primary-green hover:text-primary-green transition'
        >
          {s.label}
        </button>
      ))}
    </div>
  </div>
);

const Bubble = ({ message, onNavigate, onConfirm, onCancel, isExecuting }) => {
  if (message.role === 'user') {
    return (
      <div className='flex justify-end'>
        <div className='max-w-[80%] rounded-2xl rounded-tr-sm bg-primary-green px-3.5 py-2 text-sm font-medium text-primary-green-fg shadow-sm'>
          {message.text}
        </div>
      </div>
    );
  }
  if (message.error) {
    return (
      <div className='rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300'>
        {message.error}
      </div>
    );
  }
  if (message.help) {
    return (
      <div className='rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200'>
        {message.help}
      </div>
    );
  }
  if (message.tool === 'preview_action') {
    if (message.cancelled) {
      return (
        <div className='rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#0F1A2A] px-3 py-2 text-xs text-gray-500 dark:text-gray-400'>
          Preview dismissed.
        </div>
      );
    }
    if (message.executed) {
      return (
        <div className='rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-xs text-emerald-800 dark:text-emerald-200'>
          Mutation applied{' '}
          {message.auditLogId ? (
            <span className='font-mono text-[11px] opacity-80'>
              · audit {message.auditLogId.slice(0, 8)}
            </span>
          ) : null}
          .
        </div>
      );
    }
    return (
      <div className='flex justify-start'>
        <div className='max-w-[92%] w-full'>
          <PreviewCard
            preview={message.preview}
            params={message.previewParams}
            isExecuting={isExecuting}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
          {message.executeError ? (
            <p className='mt-1 text-[11px] text-red-600 dark:text-red-400'>{message.executeError}</p>
          ) : null}
        </div>
      </div>
    );
  }
  return (
    <div className='flex justify-start'>
      <div className='max-w-[92%] w-full'>
        <ToolResultCard
          toolName={message.tool}
          result={message.result}
          onNavigate={onNavigate}
        />
      </div>
    </div>
  );
};

export default ChatDock;
